import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import axios from 'axios';
import availablePermissions from '@/constants/temp-permissions.json';
import helpers from '@/helpers/helpers';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import api from '@/lib/Api';

const MASTER_PASSWORD_REENTRY_AFTER_CREATE = 'master_password_reentry_after_create';

function readMasterPasswordReentryAfterCreate() {
  try {
    return sessionStorage.getItem(MASTER_PASSWORD_REENTRY_AFTER_CREATE) === '1';
  } catch {
    return false;
  }
}

export const useUserStore = defineStore('user', () => {
  const showEmailModal = ref(false);
  const masterPasswordReentryAfterCreate = ref(readMasterPasswordReentryAfterCreate());
  const fetchingUser = ref(false);
  const user = ref(null);
  const hasKey = ref(false);
  const key = ref(null);
  const email = ref(null);
  const name = ref(null);
  const client_contact_email = ref(null);
  const client_contact_name = ref(null);
  const initialized = ref(false);
  const activeClient = computed(() => user.value?.active_client_id);
  const currentClient = computed(() => user.value?.client);
  const activeClientAbility = computed(() => (user.value?.abilities?.length ? user.value.abilities[0] : null));
  const userAccess = ref({ permissions: [], roles: [], subscriptions: [] });
  const isAdmin = computed(() => user.value?.is_admin);
  const isClientContact = computed(() => userAccess.value.roles.includes('client_contact'));
  const mainAbility = computed(() => (isAdmin.value ? 'admin' : activeClientAbility.value));
  const loggedIn = computed(() => user.value?.id > 0);
  const clients = computed(() =>
    user.value?.clients.map((client) => ({
      id: client?.id,
      name: client?.name,
      abilities: client?.abilities,
    })),
  );
  const isDelegatedUser = computed(() => userAccess.value?.roles[0] === 'delegated_user');
  const router = useRouter();
  const { locale } = useI18n();
  const increaseLoginTime = ref(false);
  const statements = ref({ data_room: [], insider_list: [], advisory_list: [] });

  async function updateIncreaseLoginTime(value) {
    try {
      const { data } = await axios.post('/api/auth/me/increase-login-time/toggle', {
        password: key.value,
      });
      const next = typeof data?.increase_login_time === 'boolean' ? data.increase_login_time : value;
      increaseLoginTime.value = next;
      if (user.value) {
        user.value.increase_login_time = next;
      }
    } catch (err) {
      helpers.displayAllErrorsAsToasts(err);
    }
  }

  async function fetchUser() {
    try {
      const { data } = await axios.get('/api/user/me');
      if (data?.id > 0) {
        user.value = data;
        hasKey.value = data.has_key;
        user.value.permissions = availablePermissions.permissions;
        increaseLoginTime.value = !!data.increase_login_time;
      }
    } catch (err) {
      /* console.log(err);*/
    }
  }

  async function fetchPendingShareRequests() {
    try {
      await axios.post('/api/util/pending-share-requests', { password: key.value });
    } catch (err) {
      /* console.log(err);*/
    }
  }

  async function requestClientDecryption() {
    try {
      const { data } = await axios.post('/api/client/share-with-me', {
        password: key.value,
        modelId: user.value?.active_client_id,
      });
      await requestClientUserDecryption();
    } catch (err) {
      /* console.log(err);*/
    }
  }

  async function requestClientUserDecryption() {
    try {
      const { data2 } = await axios.post('/api/share-request/client-user', {
        password: key.value,
        modelId: user.value?.client_user_id,
      });
    } catch (err) {
      /* console.log(err);*/
    }
  }

  async function fetchRolesAndPermissions() {
    try {
      const { data } = await axios.post('/api/auth/me/permissions', { password: key.value });
      userAccess.value = data;
    } catch (err) {
      /* console.log(err);*/
    }
  }

  async function fetchDecryptedUser() {
    try {
      fetchingUser.value = true;

      let masterKey = key.value;
      if (!masterKey) masterKey = localStorage.getItem('master_key') || null;
      key.value = masterKey;
      if (!masterKey) return;
      let emailAddress = email.value;
      if (!emailAddress) {
        emailAddress = localStorage.getItem('email');
      }
      if (
        !emailAddress ||
        emailAddress.length === 0 ||
        !emailAddress.includes('@') ||
        emailAddress === 'undefined' ||
        emailAddress === null ||
        emailAddress === 'null'
      ) {
        showEmailModal.value = true;
        return;
      }
      const { data } = await axios.post('/api/auth/me', { password: masterKey, email: emailAddress });
      // if (!emailAddress) {
      emailAddress = data.email.includes('@') ? data.email : emailAddress;
      localStorage.setItem('email', emailAddress);
      // }
      email.value = emailAddress;
      name.value = data.name;

      if (data?.id > 0) {
        user.value = data;
        client_contact_email.value = data.client_contact_email;
        client_contact_name.value = locale.value === 'hu' ? data.client_contact_name_hu : data.client_contact_name;
        user.value.permissions = availablePermissions.permissions;
        user.value.decrypted = true;
        increaseLoginTime.value = !!data.increase_login_time;
      }
      fetchingUser.value = false;
    } catch (err) {
      /* console.log(err);*/
      fetchingUser.value = false;
    }
  }

  function setMasterPasswordReentryAfterCreate(value) {
    masterPasswordReentryAfterCreate.value = value;
    try {
      if (value) sessionStorage.setItem(MASTER_PASSWORD_REENTRY_AFTER_CREATE, '1');
      else sessionStorage.removeItem(MASTER_PASSWORD_REENTRY_AFTER_CREATE);
    } catch {
      /* ignore */
    }
  }

  function clearMasterPasswordReentryAfterCreate() {
    setMasterPasswordReentryAfterCreate(false);
  }

  async function logout() {
    try {
      await axios.post('/api/auth/logout');
      return true;
    } catch (err) {
      /* console.log(err);*/
      return false;
    } finally {
      clearMasterPasswordReentryAfterCreate();
      user.value = null;
      increaseLoginTime.value = false;
      setInitialized(false);

      localStorage.removeItem('access_token');
      axios.defaults.headers.common['Authorization'] = '';

      localStorage.removeItem('master_key');
      key.value = null;
      localStorage.removeItem('email');
      email.value = null;
      await router.replace('/');
    }
  }

  function setInitialized(value) {
    initialized.value = value;
  }

  function setUser(value) {
    user.value = value;
  }

  async function testMasterPassword(value) {
    await axios.post('/api/keys/test', { password: value });
    key.value = value;
  }

  async function createMasterKey(payload) {
    try {
      localStorage.removeItem('master_key');
      await axios.post('/api/keys/create', {...payload, locale: locale.value });
      key.value = payload.password;
      setMasterPasswordReentryAfterCreate(true);
      helpers.successToast('Master password is created successfully');
      return true;
    } catch (err) {
      helpers.displayAllErrorsAsToasts(err);
      return false;
    }
  }

  async function editMasterKey(payload) {
    try {
      await axios.put('/api/keys/update-password', payload);
      key.value = payload.password;
      helpers.successToast('Master password is updated successfully');
      localStorage.setItem('master_key', payload.password);
      return true;
    } catch (err) {
      helpers.displayAllErrorsAsToasts(err);
      return false;
    }
  }

  async function switchUser(id) {
    await axios.post('/api/user/switch-client', { client_id: id, password: key.value });
  }

  async function updateUserInfo(payload) {
    await axios.post('/api/user/update/me', { password: key.value, ...payload });
  }

  async function getActiveClientLastChangeDate() {
    const { data } = await axios.post('/api/client/' + activeClient.value, { password: key.value });
    return data.last_change_date;
  }
  async function getRepresentativesAndBoardMembers() {
    const { data } = await axios.post(
      '/api/client/' + activeClient.value + '/representatives-and-supervising-board-members',
      { search: user.value.client.tax_number, password: key.value },
    );
    return data;
  }
  async function fetchStatements() {
    if (!user.value) return { data_room: [], insider_list: [], advisory_list: [] };
    try {
      const { data } = await axios.post('/api/user/statement-statuses', {
        password: key.value,
        userIds: [user.value.id],
      });
      const stmts = data.statements || {};
      statements.value = {
        data_room: stmts.data_room || [],
        insider_list: stmts.insider_list || [],
        advisory_list: stmts.advisory_list || [],
      };
      return statements.value;
    } catch (e) {
      return { data_room: [], insider_list: [], advisory_list: [] };
    }
  }

  async function isThisMyEmail(emailAddress) {
    let masterKey = key.value;
    if (!masterKey) masterKey = localStorage.getItem('master_key') || null;

    if (!masterKey) return false;
    const { data } = await axios.post('/api/user/is-this-my-email', { password: masterKey, email: emailAddress });
    return data.is_this_my_email;
  }

  async function removeMasterKey() {
    key.value = null;
    localStorage.removeItem('master_key');
  }

  async function permanentDeleteUser(userId) {
    const { data } = await axios.post('/api/user/delete/' + userId, { password: key.value });
    return data.message;
  }

  return {
    fetchingUser,
    user,
    masterPasswordReentryAfterCreate,
    initialized,
    activeClient,
    activeClientAbility,
    userAccess,
    isAdmin,
    mainAbility,
    loggedIn,
    clients,
    key,
    hasKey,
    email,
    name,
    isDelegatedUser,
    currentClient,
    client_contact_email,
    client_contact_name,
    setUser,
    fetchUser,
    fetchPendingShareRequests,
    requestClientDecryption,
    requestClientUserDecryption,
    fetchRolesAndPermissions,
    fetchDecryptedUser,
    logout,
    setInitialized,
    testMasterPassword,
    clearMasterPasswordReentryAfterCreate,
    createMasterKey,
    editMasterKey,
    switchUser,
    updateUserInfo,
    getActiveClientLastChangeDate,
    fetchStatements,
    increaseLoginTime,
    updateIncreaseLoginTime,
    removeMasterKey,
    isClientContact,
    statements,
    getRepresentativesAndBoardMembers,
    showEmailModal,
    isThisMyEmail,
    permanentDeleteUser,
  };
});
